import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const res = await axios.post(`${API}/auth/refresh`, null, {
            params: { refresh_token: refreshToken }
          });
          localStorage.setItem('access_token', res.data.access_token);
          localStorage.setItem('refresh_token', res.data.refresh_token);
          error.config.headers.Authorization = `Bearer ${res.data.access_token}`;
          return api(error.config);
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Funzione standalone: verifica e reindirizza se ci sono documenti da accettare.
// Usata sia da fetchUser (sessioni legacy/app-load) che implicitamente da login().
// NON escludiamo /login: il redirect deve avvenire anche subito dopo il login.
// Escludiamo solo /accetta-termini (anti-loop) e /legal/* (lettura doc).
async function checkLegalAcceptanceInline() {
  try {
    const res = await api.get('/legal/check-acceptance');
    if (res.data.needs_acceptance && res.data.slugs_to_accept?.length > 0) {
      const path = window.location.pathname;
      if (!path.startsWith('/legal') && !path.startsWith('/accetta-termini')) {
        window.location.href = `/accetta-termini?slugs=${res.data.slugs_to_accept.join(',')}`;
      }
    }
  } catch (err) {
    console.error('[Legal] Controllo accettazione fallito:', err?.response?.status, err?.message);
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      // Controlla accettazione legale anche per sessioni già attive (utenti legacy)
      await checkLegalAcceptanceInline();
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('access_token', res.data.access_token);
    localStorage.setItem('refresh_token', res.data.refresh_token);
    await fetchUser();
    // fetchUser chiama già checkLegalAcceptanceInline, non serve richiamarlo
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      fetchUser,
      isAuthenticated: !!user,
      emailVerified: user?.email_verified ?? true,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { api };
